/*  Shared  */
import {
    WORKATO_BASE_URLS,
    WORKATO_REGIONS
} from 'application/Shared/Constants/Workato';
import { Payload } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';
import { t } from 'i18next';

/*  React  */
import { useCallback, useEffect, useState } from 'react';

export const useFetchEmbeddedCustomerInfo = (platformTenantId) => {
    const [loading, setLoading] = useState(true);
    const [jwt, setJwt] = useState(null);
    const [region, setRegion] = useState(null);
    const [baseUrl, setBaseUrl] = useState(null);

    useEffect(() => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get(
                    `/workato/embedded/connect/${platformTenantId}`,
                    {
                        cancelToken: source.token
                    }
                );
                setJwt(response.data?.jwt);

                const workatoRegiom = WORKATO_REGIONS[response.data?.region];
                const resolvedBaseUrl = WORKATO_BASE_URLS[workatoRegiom];
                setBaseUrl(resolvedBaseUrl);
                setRegion(workatoRegiom);
            } catch (error) {
                if (axios.isCancel(error)) {
                    return;
                }
                debug.e('Workato. Error generating JWT.', error);
            }
            setLoading(false);
        };
        fetchData();

        return () => {
            // Cancel the request when the component unmounts
            source.cancel();
        };
    }, [platformTenantId]);

    return {
        baseUrl,
        jwt,
        loading,
        region
    };
};

export const useActivateRecipeAsync = () => {
    const activateRecipe = useCallback(
        async (platformTenantId, workatoProvider) => {
            let error;
            let success;
            try {
                const response = await axios.put(
                    `/workato/embedded/recipe/activate`,
                    { platformTenantId, workatoProvider }
                );
                if (!response.data.data?.success) {
                    debug.e('Unable to activate recipes.');
                } else {
                    success = response.data.data.success;
                }
            } catch (err) {
                error = err;
                debug.e(
                    'Error getting Workato recipe activation information.',
                    err
                );
            }
            return {
                error,
                success
            };
        },
        []
    );
    return { activateRecipe };
};
/**
 * Hook to fetch Workato customer connections
 * @param {Object} params - The parameters object
 * @param {string} params.platformTenantId - Platform tenant ID to fetch connections for
 * @param {string} [params.applicationId] - Application ID to fetch connections for
 * @returns {Object<connections<Array<WorkatoConnection>>, loading, refetchData>} Response object containing:
 * @returns {Array<WorkatoConnection>} response.connections - Array of customer connections
 * @returns {boolean} response.loading - Loading state of the fetch operation
 * @returns {Function} response.refetchData - Function to trigger a refresh of the connections data
 */
export const useFetchCustomerConnections = ({
    platformTenantId,
    applicationId
}) => {
    const [loading, setLoading] = useState(true);
    const [connections, setConnections] = useState([]);
    const [shouldRefetchData, setShouldRefetch] = useState({});

    const refetchData = () => {
        setShouldRefetch({});
    };

    useEffect(() => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        const fetchData = async () => {
            setLoading(true);
            if (!platformTenantId) {
                setLoading(false);
                return;
            }

            const params = { applicationId, platformTenantId };

            try {
                const response = await axios.get(
                    `/workato/embedded/connections`,
                    { cancelToken: source.token, params }
                );
                setConnections(response.data?.data?.result || []);
            } catch (error) {
                debug.e('Error fetching customer connections.', error);
            }
            setLoading(false);
        };
        fetchData();
    }, [platformTenantId, applicationId, shouldRefetchData]);

    return {
        connections,
        loading,
        refetchData
    };
};

export const useUpdateCustomerManifests = () => {
    const updateManifest = useCallback(
        /**
         * Updates customer manifests for Workato integration
         * @async
         * @param {Object} params - The parameters object
         * @param {boolean} [params.importOnly] - Flag that controls whether to sync workato workspace to ASM environment. This should be true only if new endpoints are added
         * @param {string[]} [params.platformTenantIdArray] - Array of platform tenant IDs to update. If null, updates all tenants with Workato workspace
         * @param {string} [params.manifestFilePath] - Path to manifest file. If null, uses default manifest from S3
         * @param {string} [params.region] - Workato region for the update
         * @param {Function} params.setLoading - Function to set loading state
         * @returns {Promise<{data, error}>} response containing response data and any error
         * @returns {Object} response.data - Response data from the update
         * @returns {Error} response.error - Error object if update fails
         */
        async ({
            importOnly,
            platformTenantIdArray,
            manifestFilePath,
            region,
            setLoading
        }) => {
            let data = null;
            let error = null;

            // If manifestFilePath is null, the API will use the default manifest stored in S3
            const payload = { importOnly, manifestFilePath, region };

            // check that either both manifestFilepath and Region are set in or neither
            if (
                (manifestFilePath && !region) ||
                (!manifestFilePath && region)
            ) {
                error = new Error(
                    'Both manifestFilePath and region must be provided together'
                );
                debug.e('Invalid manifest update parameters', error);
                setLoading(false);
                return { data, error };
            }
            // If platformTenantIdArray is null, the manifest will update for all tenants that
            // have a Workato workspace
            if (platformTenantIdArray?.length) {
                payload.platformTenantId = Payload.idString(
                    platformTenantIdArray
                );
            }
            setLoading(true);
            try {
                const response = await axios.put(
                    '/workato/embedded/update',
                    payload
                );
                data = response.data;
            } catch (err) {
                error = err;
                const errMsg =
                    err?.data?.message ||
                    t('GlobalAdmin.Applications.saasOps.updateManifestError');
                debug.e('Error starting manifest update.', errMsg);
            }
            setLoading(false);
            return {
                data,
                error
            };
        },
        []
    );
    return { updateManifest };
};

export const useOnConnectionSuccessAsync = () => {
    const onConnectionSuccess = useCallback(
        async (platformTenantId, workatoProvider) => {
            try {
                await axios.post('/workato/connectionSuccess', {
                    platformTenantId,
                    workatoProvider
                });
            } catch (error) {
                debug.e('Error on connection success.', error);
            }
        },
        []
    );
    return { onConnectionSuccess };
};
